
import { useState, useEffect } from 'react';
import { Clock, Users, Star, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import AnimatedCounter from '@/components/AnimatedCounter';

const DemandIndicatorsSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 7,
    hours: 12,
    minutes: 34
  });

  const waitlistCount = 742;
  const maxSlots = 1000;
  const progressPercentage = (waitlistCount / maxSlots) * 100;

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59 };
        }
        return prev;
      });
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="bg-white py-20">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-deep-blue mb-4">
            Trusted by Early Adopters
          </h3>
          <p className="text-deep-blue/70 text-lg max-w-2xl mx-auto">
            Join hundreds of forward-thinking individuals building better habits with Show Up!
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Waitlist Counter */}
          <div className="bg-gradient-to-br from-soft-blue/30 to-white rounded-2xl p-8 shadow-sm border border-soft-blue/50 text-center">
            <div className="w-16 h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-vibrant-orange" />
            </div>
            <div className="text-3xl font-bold text-deep-blue mb-2">
              <AnimatedCounter target={waitlistCount} suffix="" />
            </div>
            <p className="text-deep-blue/70 font-medium">People already on the waitlist</p>
            <Badge variant="secondary" className="mt-3 bg-vibrant-orange/10 text-vibrant-orange border-vibrant-orange/20">
              Growing daily
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="bg-gradient-to-br from-soft-blue/30 to-white rounded-2xl p-8 shadow-sm border border-soft-blue/50">
            <div className="w-16 h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-vibrant-orange" />
            </div>
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-deep-blue mb-1">
                <AnimatedCounter target={Math.round(progressPercentage)} suffix="%" />
              </div>
              <p className="text-deep-blue/70 text-sm">Beta slots filled</p>
            </div>
            <Progress value={progressPercentage} className="h-3 mb-3" />
            <p className="text-center text-deep-blue/60 text-sm">
              <span className="font-semibold text-vibrant-orange">{maxSlots - waitlistCount}</span> spots remaining
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="bg-gradient-to-br from-soft-blue/30 to-white rounded-2xl p-8 shadow-sm border border-soft-blue/50 md:col-span-2 lg:col-span-1">
            <div className="w-16 h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-vibrant-orange" />
            </div>
            <div className="text-center">
              <p className="text-deep-blue/70 text-sm mb-3">Early access closes in:</p>
              <div className="flex justify-center gap-2 mb-3">
                <div className="bg-vibrant-orange text-white rounded-lg px-3 py-2 min-w-[60px]">
                  <div className="text-xl font-bold">{timeLeft.days}</div>
                  <div className="text-xs">Days</div>
                </div>
                <div className="bg-vibrant-orange text-white rounded-lg px-3 py-2 min-w-[60px]">
                  <div className="text-xl font-bold">{timeLeft.hours}</div>
                  <div className="text-xs">Hours</div>
                </div>
                <div className="bg-vibrant-orange text-white rounded-lg px-3 py-2 min-w-[60px]">
                  <div className="text-xl font-bold">{timeLeft.minutes}</div>
                  <div className="text-xs">Mins</div>
                </div>
              </div>
              <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
                Limited time
              </Badge>
            </div>
          </div>
        </div>

        {/* Beta Founding Member Section */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-vibrant-orange/10 to-vibrant-orange/5 rounded-full px-6 py-3 border border-vibrant-orange/20">
            <Star className="w-5 h-5 text-vibrant-orange fill-current" />
            <span className="text-deep-blue font-semibold">Beta Founding Member</span>
            <Badge variant="outline" className="border-vibrant-orange text-vibrant-orange">
              First 1,000
            </Badge>
          </div>
          <p className="text-deep-blue/60 mt-3 max-w-md mx-auto">
            Be among the first to experience Show Up! and get exclusive early access features.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DemandIndicatorsSection;
